import React, { useState, useEffect } from 'react';
import cloneDeep from 'lodash/cloneDeep';

import ExtendedCartStep1 from './components/ExtendedCartStep1';
import ExtendedCartStep2 from './components/ExtendedCartStep2';
import ExtendedCartStep3 from './components/ExtendedCartStep3';

import { useCart } from 'frontend/js/useShopifyCart';
import { recipientProperties } from 'frontend/js/helpers';

import './ExtendedCart.scss';
import { uniqueAddressIdGenerator } from "frontend/js/helpers";

import { ProductType } from './components/CartEnums';

const STEPS = [
  ExtendedCartStep1,
  ExtendedCartStep2,
  ExtendedCartStep3
]

const BUNDLE_TYPE = ProductType.BUNDLE_CONTAINER;

function constructNewLineItem (lineItem, index) {
  const isNew = !Object.hasOwn(lineItem.properties, '_recipient_name') || !Object.hasOwn(lineItem.properties, '_itemIndex');
  return {
    ...lineItem,
    isNew: isNew && index !== 0,
    properties: {
      ...recipientProperties,
      ...lineItem.properties
    }
  }
}

function groupSimilarVariants ({ cartItems, card_types, gift_wrap_types }) {
  const groupedCartItems: any = [];
  const options = {};
  const reversed = cartItems.reverse();
  const cardAssociations = new Map();
  const giftWrapAssociations = new Map();
  

  const cardAvailability = {};
  const giftWrapAvailability = {};

  if (card_types) {
    card_types.forEach(card => {
      cardAvailability[card.id] = card.available;
    });
  }
  
  if (gift_wrap_types) {
    gift_wrap_types.forEach(wrap => {
      giftWrapAvailability[wrap.id] = wrap.available;
    });
  }

  for (const cartItem of reversed) {
    // Handle card options
    if (cartItem.properties && cartItem.properties._is_card_option) {
      const cardType = cartItem.variant_id;
      const associatedItemKey = cartItem.properties._address_key;
      
      if (associatedItemKey) {
        if (cardAvailability[cardType] !== false) {
          cardAssociations.set(associatedItemKey, cardType);
        }
      }
    }
    // Handle gift wrap options
    else if (cartItem.properties && cartItem.properties._is_gift_wrap_option) {
      const giftWrapType = cartItem.variant_id;
      const associatedItemKey = cartItem.properties._address_key;
      
      if (associatedItemKey) {
        if (giftWrapAvailability[giftWrapType] !== false) {
          giftWrapAssociations.set(associatedItemKey, giftWrapType);
        }
      }
    }
    // Handle backward compatibility - some items might have gift wrap type set directly
    else if (cartItem.product_type === ProductType.GIFT_WRAPPING && cartItem.properties?._address_key) {
      const giftWrapType = cartItem.variant_id;
      const associatedItemKey = cartItem.properties._address_key;
      
      if (giftWrapAvailability[giftWrapType] !== false) {
        giftWrapAssociations.set(associatedItemKey, giftWrapType);
      }
    }
  }

  for (const cartItem of reversed) {
    const isCardType = card_types && card_types.find(type => type.id === cartItem.variant_id);
    const isGiftWrapType = gift_wrap_types && gift_wrap_types.find(type => type.id === cartItem.variant_id);
    const isVoucher = cartItem.product_type == ProductType.GIFT_CARDS;
    
    if (!isCardType && !isGiftWrapType && !isVoucher) {
      const itemsFromQuantity = [...Array(cartItem.quantity)];
      const items = itemsFromQuantity.map((_, index) => {
        const newItem = constructNewLineItem(cartItem, index);
        
        // Handle card associations
        if (cardAssociations.has(newItem.key)) {
          const cardId = cardAssociations.get(newItem.key);
          if (cardAvailability[cardId] !== false) {
            newItem.properties._card_type = cardId;
          }
        }
        
        // Handle gift wrap associations
        if (giftWrapAssociations.has(newItem.key)) {
          const giftWrapId = giftWrapAssociations.get(newItem.key);
          if (giftWrapAvailability[giftWrapId] !== false) {
            newItem.properties._gift_wrap_type = giftWrapId;
          }
        }
        
        return newItem;
      });
      
      const groupedCartItem = groupedCartItems.find((item: any) => item.variant_id === cartItem.variant_id);

      if (groupedCartItem) {
        groupedCartItem.items = groupedCartItem.items.concat(items);
      } else {
        groupedCartItems.push({
          variant_id: cartItem.variant_id,
          product_title: cartItem.product_title,
          price: cartItem.price,
          image: cartItem.image,
          items
        });
      }
    } else {
      // Skip unavailable cards or gift wraps
      if ((isCardType && cardAvailability[cartItem.variant_id] === false) || 
          (isGiftWrapType && giftWrapAvailability[cartItem.variant_id] === false)) {
        continue;
      }
      
      options[cartItem.variant_id] = cartItem;
      
      if (isCardType || isGiftWrapType) {
        options[cartItem.variant_id].associatedItems = [];
        
        // Add associated items for cards
        if (isCardType) {
          for (const [itemKey, cardType] of cardAssociations.entries()) {
            if (cardType === cartItem.variant_id) {
              options[cartItem.variant_id].associatedItems.push(itemKey);
            }
          }
        }
        
        // Add associated items for gift wraps
        if (isGiftWrapType) {
          for (const [itemKey, giftWrapType] of giftWrapAssociations.entries()) {
            if (giftWrapType === cartItem.variant_id) {
              options[cartItem.variant_id].associatedItems.push(itemKey);
            }
          }
        }
      }
    }
  }

  return { groupedCartItems, options }
}

export default function ExtendedCart({
  customer,
  card_types,
  gift_wrap_types,
  hampers,
  settings,
  shipping_id,
  gift_wrap_enabled,
  products_with_card_options,
  translations,
  shipping_rates,
  logged_in_threshold,
  logged_out_threshold,
  free_price_bypass,
  enable_multi
}) {
  const { cart, cartMetafields } = useCart();
  const { groupedCartItems, options} = groupSimilarVariants({
    cartItems: cart.items,
    card_types,
    gift_wrap_types
  });

  const [deliveryMap, setDeliveryMap] = useState({});
  const [ chilledDeliveryMap,  setChilledDeliveryMap ] = useState({});
  const [cartItems, setCartItems] = useState(groupedCartItems);
  const [optionsInCart, setOptionsInCart] = useState(options);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage ] = useState("");
  const [hasAddOns, setHasAddOns] = useState(!!Object.keys(options).length);
  const [ isReviewModalOpen, setReviewModalOpen] = useState(false);

  const [address, setAddress] = useState({
    "address[address1]": "173 Roseburn Place Invercargill Airport Invercargill",
    "address[address2]": "",
    "address[city]": "",
    "address[company]": "Company B.",
    "address[country]": "New Zealand",
    "address[first_name]": "Dev",
    "address[last_name]": "Test 3",
    "address[phone]": "",
    "address[province]": "",
    "address[zip]": "9810",
    "delivery_instructions": "",
    'address[businessAddressType]': false,
    'address[residentialAddressType]': true
  });

  const [multiAddress, setMultiAddress] = useState({});

  const uniqueAddresses = {};

  const addressesObject = {};
  
  // Loop over each cart item to build the address structure
  cart.items.forEach(item => {
    const { id, properties } = item;
    const key = item.key;
  
    // Create the address structure based on properties
    addressesObject[key] = {
      "address[address1]": properties._shipping_address1,
      "address[address2]": properties._shipping_address2,
      "address[city]": properties._shipping_city,
      "address[company]": properties._shipping_company,
      "address[country]": properties._shipping_country,
      "address[first_name]": properties._shipping_first_name,
      "address[last_name]": properties._shipping_last_name,
      "address[phone]": properties._shipping_phone,
      "address[province]": properties._shipping_province,
      "address[zip]": properties._shipping_zip
    };
  });



  for (const address of Object.values(addressesObject)) {
    // Create a unique identifier based on relevant address fields
     const uniqueId = uniqueAddressIdGenerator(address["address[address1]"], address["address[city]"], address["address[province]"], address["address[zip]"], address["address[country]"]);
    if (!uniqueAddresses[uniqueId]) {
        uniqueAddresses[uniqueId] = address;
    }
}

  const shippingTypes: any = [
    {
      label: translations.sent_to_one_address,
      value: 'SINGLE',
    },
    ...(enable_multi
      ? [
          {
            label: translations.sent_to_multiple_address,
            value: 'MULTI',
          },
        ]
      : [])
  ];


  const [selectedShippingType, setSelectedShippingType] = useState(shippingTypes[0].value);


  const [currentStep, setCurrentStep] = useState(0);
  const StepComponent = STEPS[currentStep];

  useEffect(() => {
    const { groupedCartItems, options} = groupSimilarVariants({
      cartItems: cart.items,
      card_types,
      gift_wrap_types
    });

    if (Object.keys(options).length) {
      setHasAddOns(true);
    }

    setOptionsInCart(prevOptionsInCart => {
      const newOptionsInCart = {...prevOptionsInCart};
      
      // Remove options that no longer exist in the cart
      for (const key in prevOptionsInCart) {
        if (!options[key]) {
          delete newOptionsInCart[key];
        }
      }
      
      // Add any new options from cart
      for (const key in options) {
        if (!prevOptionsInCart[key] || 
            options[key].key !== prevOptionsInCart[key].key || 
            options[key].quantity !== prevOptionsInCart[key].quantity) {
          newOptionsInCart[key] = options[key];
        }
      }
      
      return newOptionsInCart;
    });

    setCartItems((prevCartItems) => {
      if (!prevCartItems.length) return groupedCartItems.filter((cartItem) => cartItem.variant_id !== Number(shipping_id));
      return groupedCartItems.map((cartItem) => {
        const prevCartItem = prevCartItems.find(prevCartItem => prevCartItem.variant_id === cartItem.variant_id);

        if (prevCartItem) {
          const prevCartItemCopy = cloneDeep(prevCartItem);
          
          for (const item of cartItem.items) {
            const prevItemIndex = prevCartItemCopy.items.findIndex(prevItem => prevItem.key === item.key);

            if (prevItemIndex >= 0) {
              const prevItem = prevCartItem.items.splice(prevItemIndex, 1);

              if (prevItem.length) {
                item.properties = {
                  ...item.properties,
                  ...prevItem[0].properties
                }
              }
            }
          }
        }

        return cartItem;
      }).filter((cartItem) => cartItem.variant_id !== Number(shipping_id))
    });
  }, [cart, currentStep]);

  if(cart && cart.token && cart.items.length == 0) {
    return (
    <div className="container py-32 text-center">
       {translations.empty_cart}
    </div>
    )
  }

  const giftCardProducts = cart.items.filter(item => item.product_type == "Gift Cards");

  return (
    <StepComponent
      cart={cart}
      cartItems={cartItems}
      giftCardProducts={giftCardProducts}
      cartMetafields={cartMetafields}
      optionsInCart={optionsInCart}
      hasAddOns={hasAddOns}
      customer={customer}
      card_types={card_types}
      gift_wrap_types={gift_wrap_types}
      isGiftWrapEnabled={gift_wrap_enabled}
      productsWithCardOptions={products_with_card_options}
      hampers={hampers}
      settings={settings}
      shipping_id={Number(shipping_id)}
      translations={translations}
      address={address} 
      multiAddress={multiAddress} 
      uniqueAddresses={uniqueAddresses} // Mapping for unique address between all items on multi address cart
      onClickAddCards={(value) => setHasAddOns(value)}
      onCartItemsChange={(cartItems) => setCartItems(cartItems)}
      onAddressChange={(address) => setAddress(address)}
      onMultiAddressChange={(multiAddress) => setMultiAddress(multiAddress)}
      onStepChange={(stepIndex) => setCurrentStep(stepIndex)}
      deliveryMap={deliveryMap}
      setDeliveryMap={setDeliveryMap}
      chilledDeliveryMap={chilledDeliveryMap} 
      setChilledDeliveryMap={setChilledDeliveryMap}
      setSelectedShippingType={setSelectedShippingType}
      selectedShippingType={selectedShippingType}
      shippingTypes={shippingTypes}
      bundleType={BUNDLE_TYPE}
      shipping_rates={shipping_rates}
      error={error}
      setError={setError}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      logged_in_threshold={logged_in_threshold}
      logged_out_threshold={logged_out_threshold}
      free_price_bypass={free_price_bypass}
      isReviewModalOpen={isReviewModalOpen}
      setReviewModalOpen={setReviewModalOpen}
    />
  );
};
